import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CaseDialog, { StatusChip } from "./CaseDialog";
import ClaimResponseDialog from "./ClaimResponseDialog";
import { claimErrorText, formatDay, rightsClaimApi } from "./rightsClaimApi";

const ROLE_LABEL = { filer: "Filed by you", respondent: "Against your sound" };

// Cases the signed-in person filed or that were filed against their sounds, optionally limited to some case types
// (Artist Studio > Rights & Legal shows notices and claims on separate tabs).
// `variant="claims"` is the Claims Received table (FSD 6.8.3): Track, Claimant, Received, Status, and a Respond button while the claim is Pending.
export default function CaseList({ types, empty, variant, columns }) {
  const claims = variant === "claims";
  const head = columns || (claims ? ["Track", "Claimant", "Received", "Status", ""] : ["Case", "Sound", "Type", "Status", "Received", ""]);
  const [state, setState] = useState({ loading: true, items: [], error: "" });
  const [openId, setOpenId] = useState(null);
  const [responding, setResponding] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    rightsClaimApi.mine({ limit: 50 }).then(
      (data) => setState({ loading: false, items: data?.items || [], error: "" }),
      (e) => setState({ loading: false, items: [], error: claimErrorText(e, "We couldn't load your cases.") })
    );
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = types ? state.items.filter((i) => types.includes(i.claim_type)) : state.items;
  return (
    <div data-testid="case-list">
      <div className="rounded-xl border border-gray-200 bg-[#f3f7f4] overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: "#095228" }}>
            <tr>{head.map((c) => <th key={c} className="px-5 py-3 whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody className="bg-white">
            {state.loading && <tr className="border-t border-gray-200"><td colSpan={head.length} className="px-5 py-4 text-gray-500">Loading…</td></tr>}
            {state.error && (
              <tr className="border-t border-gray-200"><td colSpan={head.length} className="px-5 py-4 text-red-600" role="alert">{state.error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></td></tr>
            )}
            {!state.loading && !state.error && items.length === 0 && (
              <tr className="border-t border-gray-200"><td colSpan={head.length} className="px-5 py-4 text-gray-500">{empty}</td></tr>
            )}
            {claims && items.map((i) => (
              <tr key={i.id} className="border-t border-gray-200" data-testid={`case-row-${i.id}`}>
                <td className="px-5 py-3">{i.track?.id ? <Link to={`/sound/${i.track.id}`} className="font-semibold text-gray-900 hover:underline">{i.track.title || `Sound #${i.track.id}`}</Link> : "—"}<span className="block text-xs text-gray-500">{i.case_number}{ROLE_LABEL[i.role] ? ` · ${ROLE_LABEL[i.role]}` : ""}</span></td>
                <td className="px-5 py-3">{i.role === "filer" ? "You" : i.claimant_name || "—"}</td>
                <td className="px-5 py-3 whitespace-nowrap">{formatDay(i.received_at)}</td>
                <td className="px-5 py-3">
                  <StatusChip status={i.status} display={i.display_status} />
                  {i.can_respond && i.due_at && <span className="block text-xs text-amber-800 mt-1" data-testid="respond-by">Respond by {formatDay(i.due_at)}</span>}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {i.can_respond && <button type="button" onClick={() => setResponding(i)} className="mr-4 rounded-full px-4 py-1.5 text-sm font-semibold text-white" style={{ backgroundColor: "#095228" }} data-testid={`respond-${i.id}`}>Respond</button>}
                  <button type="button" onClick={() => setOpenId(i.id)} className="font-semibold hover:underline" style={{ color: "#095228" }}>View</button>
                </td>
              </tr>
            ))}
            {!claims && items.map((i) => (
              <tr key={i.id} className="border-t border-gray-200" data-testid={`case-row-${i.id}`}>
                <td className="px-5 py-3 font-semibold text-gray-900 whitespace-nowrap">{i.case_number || `#${i.id}`}</td>
                <td className="px-5 py-3">{i.track?.id ? <Link to={`/sound/${i.track.id}`} className="hover:underline">{i.track.title || `Sound #${i.track.id}`}</Link> : "—"}</td>
                <td className="px-5 py-3 whitespace-nowrap">{i.claim_label}<span className="block text-xs text-gray-500">{ROLE_LABEL[i.role] || ""}</span></td>
                <td className="px-5 py-3"><StatusChip status={i.status} />{i.needs_response && <span className="ml-2 text-xs font-semibold text-red-700">Reply needed</span>}</td>
                <td className="px-5 py-3 whitespace-nowrap">{formatDay(i.received_at)}</td>
                <td className="px-5 py-3 text-right"><button type="button" onClick={() => setOpenId(i.id)} className="font-semibold hover:underline" style={{ color: "#095228" }}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openId && <CaseDialog caseId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      {responding && <ClaimResponseDialog claim={responding} onClose={() => setResponding(null)} onDone={() => { setResponding(null); load(); }} />}
    </div>
  );
}
